import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import type { TripsWorldMapGuide } from '../components/TripsWorldMap';
import { frontendConfig } from '../utils/frontendConfig';
import { supportsWebGL2 } from '../utils/webgl';

// Mounts the world map of a hub (#trips-world-map-root with data-guides JSON, data-label and data-noun): every
// guide on /travel, stay on /stay, route on /adventures. The vector
// map (PaperTripsWorldMap, MapLibre) with a tile host and WebGL2, else - or when it can't load - the Leaflet
// map. The map code and tiles load when the map is about to scroll into view.

let root: Root | null = null;
let currentContainer: HTMLElement | null = null;
let observer: IntersectionObserver | null = null;
// The "Loading map..." placeholder from the server. Put back on cleanup, so the page Turbo caches keeps
// its height - an empty, zero-height root restored by "back" may never scroll into view to remount.
let placeholder = '';

function cleanup() {
  observer?.disconnect();
  observer = null;
  root?.unmount();
  root = null;
  if (currentContainer && placeholder) currentContainer.innerHTML = placeholder;
  currentContainer = null;
}

// The map's accessible name and what a group's items are called, from the root
function names(container: HTMLElement) {
  return { label: container.dataset.label || 'Map of every travel guide', noun: container.dataset.noun || 'guides' };
}

async function mountLeaflet(container: HTMLElement, guides: TripsWorldMapGuide[], slugs: string[] | null) {
  const { LeafletTripsWorldMap } = await import('../components/TripsWorldMap/LeafletTripsWorldMap');
  if (container !== currentContainer || !root) return;

  root.render(
    <React.StrictMode>
      <LeafletTripsWorldMap guides={guides} initialSlugs={slugs} {...names(container)} />
    </React.StrictMode>,
  );
}

async function mount(container: HTMLElement) {
  const raw = container.dataset.guides;
  if (!raw) return;

  const guides = JSON.parse(raw) as TripsWorldMapGuide[];
  // content_browse_controller keeps the current filter here (e.g. ?country=italy) - it can run before the map exists
  const initialSlugs = container.dataset.slugs ? (JSON.parse(container.dataset.slugs) as string[]) : null;
  const host = frontendConfig('map-tiles-host');

  if (!host || !supportsWebGL2()) {
    root = createRoot(container);
    await mountLeaflet(container, guides, initialSlugs);
    return;
  }

  let PaperTripsWorldMap: typeof import('../components/TripsWorldMap/PaperTripsWorldMap').PaperTripsWorldMap;
  try {
    ({ PaperTripsWorldMap } = await import('../components/TripsWorldMap/PaperTripsWorldMap'));
  } catch (error) {
    console.warn('Vector map did not load, using the raster map', error);
    if (container !== currentContainer) return;
    root = createRoot(container);
    await mountLeaflet(container, guides, initialSlugs);
    return;
  }
  // The page may have changed while the chunk was loading (Turbo navigation)
  if (container !== currentContainer) return;

  root = createRoot(container);
  root.render(
    <React.StrictMode>
      <PaperTripsWorldMap
        guides={guides}
        initialSlugs={initialSlugs}
        {...names(container)}
        host={host}
        onUnavailable={(slugs) => void mountLeaflet(container, guides, slugs)}
      />
    </React.StrictMode>,
  );
}

function setup() {
  const container = document.getElementById('trips-world-map-root');
  if (!container || container === currentContainer) return;

  cleanup();
  currentContainer = container;
  placeholder = container.innerHTML;

  observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      observer?.disconnect();
      observer = null;
      void mount(container);
    },
    { rootMargin: '400px 0px' },
  );
  observer.observe(container);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setup);
} else {
  setup();
}

document.addEventListener('turbo:load', setup);
document.addEventListener('turbo:before-cache', cleanup);
document.addEventListener('turbo:before-render', cleanup);
